// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";

// Custom packages
import { EnvironmentService } from "src/app/shared/services/environment.service";

export interface Locale {
  lang: string;
  data: object;
}

@Injectable({
  providedIn: "root"
})
export class TranslationService {
  private langIds: any = [];

  /**
   * Class constructor
   */
  constructor(
    private translate: TranslateService,
    private cookieService: CookieService,
    private environmentService: EnvironmentService
  ) {
    // Add new langIds to the list
    this.translate.addLangs(["en", "it"]);
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang("en");
  }

  /**
   * Load Translation
   *
   * @since 1.0.0
   *
   * @param args: Locale[]
   */
  loadTranslations(...args: Locale[]): void {
    const locales = [...args];

    locales.forEach(locale => {
      // use setTranslation() with the third argument set to true
      // to append translations instead of replacing them
      this.translate.setTranslation(locale.lang, locale.data, true);

      this.langIds.push(locale.lang);
    });

    // add new languages to the list
    this.translate.addLangs(this.langIds);
  }

  /**
   * Setup language
   *
   * @since 1.0.0
   *
   * @param lang: any
   * @throws error if given lang is not a valid lang
   */
  setLanguage(lang?: string): void {
    if (lang) {
      const langs = this.translate.getLangs();
      if (!langs.includes(lang)) {
        throw new Error("Invalid language");
      }
      this.translate.use(this.translate.getDefaultLang());
      this.translate.use(lang);
      this.cookieService.set(
        "lang",
        lang,
        365,
        "/",
        this.environmentService.getFrontEndHost(),
        true
      );
    }
  }

  /**
   * Returns selected language
   *
   * @since 1.0.0
   */
  getSelectedLanguage(): string | null | undefined {
    if (this.translate.currentLang) {
      return this.translate.currentLang;
    }
    return (
      this.cookieService.get("language") || this.translate.getDefaultLang()
    );
  }
}
